import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import { ConfigurationStatusEnum } from '../../../../definitions/enums/settings/configuration-status.enum';
import { ContactInformation } from '../../../../definitions/interfaces/customer/contact-information';
import { ContractInformation } from '../../../../definitions/interfaces/customer/contract-information';
import { CustomerConfig } from '../../../../definitions/interfaces/customer/customer-config';
import { CustomerDeliveryAddress } from '../../../../definitions/interfaces/customer/customer-delivery-address';
import { AdditionalMarkingConfig } from '../../../../definitions/interfaces/customer-overview/additional-marking-config';
import {
  ApprovalManagementConfig
} from '../../../../definitions/interfaces/customer-overview/approval-management-config';
import {
  RepEvoCostCenters,
  RepEvoCostCentersStateRequest
} from '../../../../definitions/interfaces/customer-overview/cost-centers.interface';
import {
  RepEvoEmailNotifications,
  RepEvoEmailNotificationsStateRequest
} from '../../../../definitions/interfaces/customer-overview/email-notifications.interface';
import {
  GLN,
  GlnConfiguration,
  GlnConfigStateRequest
} from '../../../../definitions/interfaces/customer-overview/gln.interface';
import { PriceConfig } from '../../../../definitions/interfaces/customer-overview/price-config';
import {
  ShipToCustomer,
  ShipToCustomersConfig
} from '../../../../definitions/interfaces/customer-overview/ship-to-customers-config.interface';

export const loadCustomerConfig = createAction(
  'LOAD_CUSTOMER_CONFIG',
  props<{ configId: string }>()
);

export const loadCustomerConfigSuccess = createAction(
  'LOAD_CUSTOMER_CONFIG_SUCCESS',
  props<{ customerConfig: CustomerConfig }>()
);

export const updateDeliveryAddress = createActionGroup({
  source: '[Customer Overview] Update Delivery Address',
  events: {
    'Update': props<{ deliveryAddress: CustomerDeliveryAddress }>(),
    'Update Success': props<{ deliveryAddress: CustomerDeliveryAddress, status: ConfigurationStatusEnum }>(),
    'Update Error': emptyProps()
  }
});

export const updatePriceConfig = createActionGroup({
  source: '[Customer Overview] Update Price Config',
  events: {
    'Update': props<{ priceUpdate: PriceConfig }>(),
    'Update Success': props<{ priceUpdate: PriceConfig }>(),
    'Update Error': emptyProps()
  }
});

export const updateContactInformation = createActionGroup({
  source: '[Customer Overview] Update Contact Information',
  events: {
    'Update': props<{ contactInformation: ContactInformation }>(),
    'Update Success': props<{ contactInformation: ContactInformation, status: ConfigurationStatusEnum }>(),
    'Update Error': emptyProps()
  }
});

export const updateMarkingConfig = createActionGroup({
  source: '[Customer Overview] Update Marking Config',
  events: {
    'Update': props<{ updateData: AdditionalMarkingConfig }>(),
    'Update Success': props<{ updatedData: AdditionalMarkingConfig }>(),
    'Update Error': emptyProps()
  }
});

export const updateApprovalManagement = createActionGroup({
  source: '[Customer Overview] Update Approval Management',
  events: {
    'Update': props<{ updateData: ApprovalManagementConfig }>(),
    'Update Success': props<{ updatedData: ApprovalManagementConfig }>(),
    'Update Error': emptyProps()
  }
});

export const updateShipToCustomers = createActionGroup({
  source: '[Customer Overview] Update Ship-to Customers',
  events: {
    'Update': props<{ config: ShipToCustomersConfig }>(),
    'Update Success': props<{ configId: string, config: ShipToCustomersConfig }>(),
    'Update Error': emptyProps(),
    'Add Customer': props<{ customer: ShipToCustomer }>(),
    'Add Customer Success': props<{ configId: string }>(),
    'Set Editable Customer': props<{ customer: ShipToCustomer }>(),
    'Edit Customer': props<{ customer: ShipToCustomer }>(),
    'Edit Customer Success': props<{ configId: string }>(),
    'Delete customer': props<{ customerId: string }>(),
    'Delete Customer Success': props<{ configId: string }>()
  }
});

export const updateContractInformation = createActionGroup({
  source: '[Customer Overview] Update Contract Information',
  events: {
    'Update': props<{ contractInformation: ContractInformation }>(),
    'Update Success': props<{ updatedInformation: ContractInformation }>(),
    'Update Error': emptyProps()
  }
});

export const updateGlnConfig = createActionGroup({
  source: '[Customer Overview] Update GLN Config',
  events: {
    'Set': props<{ request: GlnConfigStateRequest }>(),
    'Set Success': props<{ customerConfig: CustomerConfig }>(),
    'Set Error': emptyProps(),
    'Add Gln': props<{ gln: GLN }>(),
    'Add Gln Success': props<{ glnConfiguration: GlnConfiguration }>(),
    'Add Gln Error': emptyProps(),
    'Edit Gln': props<{ originalGlnNumber: string, updatedGln: GLN }>(),
    'Edit Gln Success': props<{ glnConfiguration: GlnConfiguration }>(),
    'Edit Gln Error': emptyProps(),
    'Delete Gln': props<{ glnNumber: string }>(),
    'Delete Gln Success': props<{ glnConfiguration: GlnConfiguration }>(),
    'Delete Gln Error': emptyProps(),
  }
});

export const updateRepEvoEmailNotifications = createActionGroup({
  source: '[Customer Overview] Update RepEvo Email Notifications',
  events: {
    'Toggle': props<{ request: RepEvoEmailNotificationsStateRequest }>(),
    'Toggle Success': props<{ repEvoEmailNotifications: RepEvoEmailNotifications }>(),
    'Toggle Error': emptyProps(),
    'Add Recipient': props<{ emailAddress: string }>(),
    'Add Recipient Success': props<{ repEvoEmailNotifications: RepEvoEmailNotifications }>(),
    'Add Recipient Error': props<{ emailError?: string }>(),
    'Delete Recipient': props<{ emailAddress: string }>(),
    'Delete Recipient Success': props<{ repEvoEmailNotifications: RepEvoEmailNotifications }>(),
    'Delete Recipient Error': emptyProps(),
  }
});

export const updateRepEvoPriorityOrders = createActionGroup({
  source: '[Customer Overview] Update RepEvo Priority Orders',
  events: {
    'Update': props<{ active: boolean }>(),
    'Update Success': props<{ active: boolean }>(),
    'Update Error': props<{ active: boolean }>()
  }
});

export const updateRepEvoCostCenters = createActionGroup({
  source: '[Customer Overview] Update RepEvo Cost Centers',
  events: {
    'Set': props<{ request: RepEvoCostCentersStateRequest }>(),
    'Set Success': props<{ repEvoCostCenters: RepEvoCostCenters }>(),
    'Set Error': emptyProps(),
    'Add Cost Center': props<{ name: string }>(),
    'Add Cost Center Success': props<{ repEvoCostCenters: RepEvoCostCenters }>(),
    'Add Cost Center Error': emptyProps(),
    'Edit Cost Center': props<{ costCenterId: string, name: string }>(),
    'Edit Cost Center Success': props<{ repEvoCostCenters: RepEvoCostCenters }>(),
    'Edit Cost Center Error': emptyProps(),
    'Delete Cost Center': props<{ costCenterId: string }>(),
    'Delete Cost Center Success': props<{ repEvoCostCenters: RepEvoCostCenters }>(),
    'Delete Cost Center Error': emptyProps(),
  }
});

export const updateRepEvoCustomerNumber = createActionGroup({
  source: '[Customer Overview] Update RepEvo Customer Number',
  events: {
    'Update': props<{ customerNumber: string, oldCustomerNumber: string | undefined }>(),
    'Update Success': props<{ customerNumber: string }>(),
    'Update Error': props<{ customerNumber: string | undefined }>()
  }
});
